import type { ApiResponseError } from "@codesandbox/api";
import type { IPitcherClient, protocol } from "@codesandbox/pitcher-client";
import { createInitialStatus } from "@codesandbox/pitcher-client";
import { GlobalError } from "components/Error/GlobalError";
import { useEnvironmentInterface } from "environment-interface";
import {
  env,
  getDevPitcherManagerURL,
  getUsePitcherManagerThroughApi,
} from "environment-interface/env";
import { useEditor } from "features/editor/Feature";
import {
  LegacyPitcherProvider,
  PitcherProvider,
} from "features/pitcher/Feature";
import { Box, keyframes } from "prism-react";
import { getPitcherInstance } from "queries/pitcher";
import { useEffect, useState } from "react";

import { BlockingLoader } from "components/BlockingLoader";
import { useAnalytics } from "hooks/useAnalytics";
import { useEditorRouter } from "hooks/useEditorRouter";

import { EditorLayout } from "./EditorLayout";
import { OutOfCreditsButton, OutOfCreditsDescription } from "./OutOfCredits";

const SHOW_LONG_WAIT_DELAY_MS = 4000;

// The Pitcher messages are between 0-100, but we load between
// 20-75 for actual Pitcher loading
function offsetPitcherInit(
  initStatus: protocol.system.InitStatus,
): protocol.system.InitStatus {
  return {
    ...initStatus,
    progress: Math.max(20, initStatus.progress - 25),
    nextProgress: Math.max(20, initStatus.nextProgress - 25),
  };
}

export const InitPitcher: React.FC<{
  children: React.ReactNode;
  id: string;
  seamlessFork: boolean;
}> = ({ id, seamlessFork, children }) => {
  const environment = useEnvironmentInterface();
  const editor = useEditor();
  const router = useEditorRouter();
  const isImport = router.branch.queryParams.import.get();
  const isCheckout = router.branch.queryParams.checkout.get();

  let initialStatusMessage = "Resuming MicroVM...";
  if (isImport) {
    initialStatusMessage = "Importing repository...";
  } else if (isCheckout) {
    initialStatusMessage = "Checking out branch...";
  }

  const [initStatus, setInitStatus] = useState<protocol.system.InitStatus>(
    offsetPitcherInit(createInitialStatus(initialStatusMessage)),
  );

  const track = useAnalytics();

  const [pitcherState, setPitcherState] = useState<
    | {
        state: "INITIALIZING";
      }
    | {
        state: "READY";
        client: IPitcherClient;
      }
    | {
        state: "ERROR";
        error: unknown;
      }
  >({
    state: "INITIALIZING",
  });

  const csbApi = environment.csbApi!;

  useEffect(() => {
    if (pitcherState.state !== "READY") {
      return;
    }

    pitcherState.client.toggleSeamlessFork(seamlessFork);
  }, [seamlessFork, pitcherState]);

  useEffect(() => {
    const instanceRequestTime = Date.now();

    environment.pitcherInstanceManager
      .getInstance(
        {
          instanceId: id,
          seamlessFork,
          requestPitcherInstance: async (branchId) => {
            const pitcherManagerResponse = await getPitcherInstance(
              csbApi,
              {},
              {
                branchId,
                pitcherManagerURL: getUsePitcherManagerThroughApi()
                  ? getDevPitcherManagerURL() ?? ""
                  : env.PUBLIC_PITCHER_MANAGER_URL,
                usePitcherManagerThroughApi: getUsePitcherManagerThroughApi(),
              },
            );

            track("pitcher_initialized", {
              vmId: id,
              duration: Date.now() - instanceRequestTime,
              bootupType: pitcherManagerResponse.bootupType,
            });

            return pitcherManagerResponse;
          },
          subscriptions: {
            client: {
              status: true,
            },
            file: {
              status: true,
              selection: true,
              ot: true,
            },
            fs: {
              operations: true,
            },
            git: {
              status: true,
              operations: true,
            },
            port: {
              status: true,
            },
            setup: {
              progress: true,
            },
            shell: {
              status: true,
            },
            system: {
              metrics: true,
            },
          },
          appId: "codesandbox",

          onFocusChange: (cb) => {
            const visibilityListener = () => {
              track("visibility", {
                isVisible: document.visibilityState === "visible",
              });
              cb(document.visibilityState === "visible");
            };

            window.document.addEventListener(
              "visibilitychange",
              visibilityListener,
            );

            return () => {
              window.document.removeEventListener(
                "visibilitychange",
                visibilityListener,
              );
            };
          },
        },
        (updatedStatus) => setInitStatus(offsetPitcherInit(updatedStatus)),
      )
      .then((client) => {
        setPitcherState({ state: "READY", client });
      })
      .catch((error: ApiResponseError) => {
        let errorMessage = error.message;
        if (error.type === "WORKSPACE_FROZEN") {
          errorMessage = "WORKSPACE_FROZEN";
        }

        setPitcherState({ state: "ERROR", error: errorMessage });
      });
  }, []);

  useEffect(() => {
    if (initStatus.message !== initialStatusMessage) {
      return;
    }

    const timeout = setTimeout(() => {
      setInitStatus({
        ...initStatus,
        message: initStatus.message + " Still booting, please wait",
      });
    }, SHOW_LONG_WAIT_DELAY_MS);

    return () => {
      clearTimeout(timeout);
    };
  }, [initStatus, initialStatusMessage]);

  switch (pitcherState.state) {
    case "INITIALIZING": {
      return (
        <EditorLayout>
          <BlockingLoader
            key={initStatus.message}
            loadingBarEnd={initStatus.nextProgress}
            loadingBarProgress={initStatus.progress}
            loadingBarSpeed={4}
            loadingBarStart={initStatus.progress}
          >
            <Box css={{ whiteSpace: "nowrap" }}>{initStatus.message}</Box>
            <Box
              css={{
                transition: "300ms ease-out all",
                position: "absolute",
                paddingTop: "$2",
                top: "100%",
                color: "$neutral-fg-low",
                width: "100%",
                fontSize: "13px",
                textWrap: "balance",
                animation: `${keyframes({
                  "0%": {
                    opacity: 0,
                  },
                  "100%": {
                    opacity: 1,
                  },
                })} 2s ease-in ${SHOW_LONG_WAIT_DELAY_MS}ms forwards`,
                opacity: 0,
              }}
            >
              MicroVM snapshots are created to boot up in less than 2 seconds
            </Box>
          </BlockingLoader>
        </EditorLayout>
      );
    }
    case "ERROR": {
      if (pitcherState.error === "WORKSPACE_FROZEN") {
        // Out of credits error
        return (
          <GlobalError
            context={{
              error: String(pitcherState.error),
              branchId: id,
              state: "ERROR",
              event: "Initial connect to Pitcher",
              originalError: pitcherState.error,
            }}
            customCause={{
              title: "Out of credits",
              description: <OutOfCreditsDescription editor={editor} />,
              recoverButton: <OutOfCreditsButton editor={editor} />,
            }}
            errorCode="WORKSPACE_FROZEN"
          />
        );
      }

      return (
        <GlobalError
          context={{
            error: String(pitcherState.error),
            branchId: id,
            state: "ERROR",
            event: "Initial connect to Pitcher",
            originalError: pitcherState.error,
          }}
          errorCode="MICROVM_NOT_STARTED"
        />
      );
    }
    case "READY": {
      return (
        <PitcherProvider client={pitcherState.client} id={id}>
          <LegacyPitcherProvider client={pitcherState.client}>
            {children}
          </LegacyPitcherProvider>
        </PitcherProvider>
      );
    }
  }
};
